import { useState, useEffect } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Warehouse, Search, Calendar, Loader2, BarChart3, ClipboardList, Clock, CheckCircle2, AlertTriangle, PackageCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, differenceInCalendarDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { listsEqualById } from '../lib/listDiff';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUTS = {
    a_prevoir: { label: 'A prevoir', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    reserve: { label: 'Reservé', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    actif: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    termine: { label: 'Terminé', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

function formatDate(d) { if (!d) return '-'; try { return format(new Date(d), 'dd/MM/yyyy', { locale: fr }); } catch { return d; } }

export default function GardeMeublePage() {
    const { can } = usePermissions();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const readOnly = !can('garde_meuble', 'write');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState(null); // null | 'all' | 'a_prevoir' | 'reserve' | 'actif' | 'termine' | 'urgent'
    const [confirmDelete, setConfirmDelete] = useState(null); // {id, name} | null

    const fetchData = (silent = false) => {
        if (!silent) setLoading(true);
        axios.get(`${API}/api/garde-meuble`, { withCredentials: true })
            .then(res => setItems(prev => listsEqualById(prev, res.data) ? prev : res.data))
            .catch(() => { if (!silent) toast.error('Erreur de chargement'); })
            .finally(() => { if (!silent) setLoading(false); });
    };
    useEffect(() => { fetchData(); }, []);

    useAutoRefresh(() => fetchData(true));

    const handleStatut = async (id, statut) => {
        try {
            await axios.patch(`${API}/api/garde-meuble/${id}/statut?statut=${statut}`, {}, { withCredentials: true });
            toast.success('Statut mis a jour');
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await axios.delete(`${API}/api/garde-meuble/${confirmDelete.id}`, { withCredentials: true });
            toast.success('Garde-meuble supprimé (déplacé dans la corbeille)');
            setConfirmDelete(null);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const isUrgent = (item) => {
        if (item.statut === 'a_prevoir' && item.date_entree) {
            const jours = differenceInCalendarDays(new Date(item.date_entree), new Date());
            return jours >= 0 && jours <= 7;
        }
        if (item.statut === 'actif' && item.date_sortie) {
            const jours = differenceInCalendarDays(new Date(item.date_sortie), new Date());
            return jours >= 0 && jours <= 7;
        }
        return false;
    };
    const urgentCount = items.filter(isUrgent).length;

    const filtered = items.filter(c => {
        if (activeFilter === 'a_prevoir' && c.statut !== 'a_prevoir') return false;
        if (activeFilter === 'reserve' && c.statut !== 'reserve') return false;
        if (activeFilter === 'actif' && c.statut !== 'actif') return false;
        if (activeFilter === 'termine' && c.statut !== 'termine') return false;
        if (activeFilter === 'urgent' && !isUrgent(c)) return false;
        if (!search) return true;
        return (c.client_nom || '').toLowerCase().includes(search.toLowerCase());
    }).sort((a, b) => (isUrgent(b) ? 1 : 0) - (isUrgent(a) ? 1 : 0));

    const total = items.length;
    const aPrevoir = items.filter(i => i.statut === 'a_prevoir').length;
    const reserve = items.filter(i => i.statut === 'reserve').length;
    const actif = items.filter(i => i.statut === 'actif').length;
    const termine = items.filter(i => i.statut === 'termine').length;

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="garde-meuble-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Garde-meuble</h1>
                    <p className="mt-1 text-slate-500">Stockage client</p>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6" data-testid="gm-stats">
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'all' ? null : 'all')} className={`text-left transition-all ${activeFilter === 'all' ? 'ring-2 ring-slate-400' : 'hover:shadow-md'}`} data-testid="gm-filter-total">
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                <BarChart3 size={20} className="text-slate-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-900" data-testid="gm-stat-total">{total}</p>
                                <p className="text-xs text-slate-500">Total</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'a_prevoir' ? null : 'a_prevoir')} className={`text-left transition-all ${activeFilter === 'a_prevoir' ? 'ring-2 ring-amber-400' : 'hover:shadow-md'}`} data-testid="gm-filter-a-prevoir">
                    <Card className="bg-white rounded-xl border border-amber-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                                <ClipboardList size={20} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-amber-700" data-testid="gm-stat-a-prevoir">{aPrevoir}</p>
                                <p className="text-xs text-slate-500">A prévoir</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'reserve' ? null : 'reserve')} className={`text-left transition-all ${activeFilter === 'reserve' ? 'ring-2 ring-blue-400' : 'hover:shadow-md'}`} data-testid="gm-filter-reserve">
                    <Card className="bg-white rounded-xl border border-blue-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                <Clock size={20} className="text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-blue-700" data-testid="gm-stat-reserve">{reserve}</p>
                                <p className="text-xs text-slate-500">Réservé</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'actif' ? null : 'actif')} className={`text-left transition-all ${activeFilter === 'actif' ? 'ring-2 ring-emerald-400' : 'hover:shadow-md'}`} data-testid="gm-filter-actif">
                    <Card className="bg-white rounded-xl border border-emerald-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                                <PackageCheck size={20} className="text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-emerald-700" data-testid="gm-stat-actif">{actif}</p>
                                <p className="text-xs text-slate-500">Actif</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'termine' ? null : 'termine')} className={`text-left transition-all ${activeFilter === 'termine' ? 'ring-2 ring-slate-400' : 'hover:shadow-md'}`} data-testid="gm-filter-termine">
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                <CheckCircle2 size={20} className="text-slate-500" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-600" data-testid="gm-stat-termine">{termine}</p>
                                <p className="text-xs text-slate-500">Terminé</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'urgent' ? null : 'urgent')} className={`text-left transition-all ${activeFilter === 'urgent' ? 'ring-2 ring-red-400' : 'hover:shadow-md'}`} data-testid="gm-filter-urgent">
                    <Card className={`bg-white rounded-xl border shadow-sm h-full ${urgentCount > 0 ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${urgentCount > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                                <AlertTriangle size={20} className={urgentCount > 0 ? 'text-red-600' : 'text-slate-400'} />
                            </div>
                            <div>
                                <p className={`text-2xl font-bold ${urgentCount > 0 ? 'text-red-600' : 'text-slate-400'}`} data-testid="gm-stat-urgent">{urgentCount}</p>
                                <p className="text-xs text-slate-500">Urgent</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
            </div>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input placeholder="Rechercher (nom)..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="gm-search" />
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <Warehouse size={20} className="text-[#2e7cd6]" />
                        Liste des garde-meubles ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">Aucun garde-meuble</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table className="min-w-[700px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Date d'entree</TableHead>
                                        <TableHead>Tarif mensuel</TableHead>
                                        <TableHead>Duree</TableHead>
                                        <TableHead>Date de sortie</TableHead>
                                        <TableHead>Statut</TableHead>
                                        {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(item => {
                                        const canEdit = item.statut === 'a_prevoir';
                                        const isAuto = item.statut === 'reserve' || item.statut === 'actif' || item.statut === 'termine';
                                        return (
                                        <TableRow key={item.id} data-testid={`gm-row-${item.id}`} className={isUrgent(item) ? 'bg-red-50/50' : ''}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                                                        <Warehouse size={20} className="text-purple-600" />
                                                    </div>
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <p className="font-medium text-slate-900 truncate">{item.client_nom}</p>
                                                        {isUrgent(item) && (
                                                            <Badge className="bg-red-500 text-white border-0 text-[10px] px-1.5 py-0 h-5 animate-pulse" data-testid={`gm-urgent-${item.id}`}>
                                                                URGENT
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar size={12} className="text-slate-400" />
                                                    <span className={`text-sm ${item.date_entree ? 'text-slate-700' : 'text-slate-400 italic'}`}>{item.date_entree ? formatDate(item.date_entree) : 'Non programmé'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-slate-700">{item.garde_meuble_tarif || '-'}</span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-slate-700">{item.garde_meuble_duree || '-'}</span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar size={12} className="text-slate-400" />
                                                    <span className={`text-sm ${item.date_sortie ? 'text-slate-700' : 'text-slate-400 italic'}`} data-testid={`gm-date-sortie-${item.id}`}>
                                                        {item.date_sortie ? formatDate(item.date_sortie) : 'Non programmé'}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {canEdit && !readOnly ? (
                                                    <Select value={item.statut} onValueChange={(v) => handleStatut(item.id, v)}>
                                                        <SelectTrigger className="w-[140px] h-8" data-testid={`gm-statut-${item.id}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="a_prevoir">
                                                                <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUTS.a_prevoir.color}`}>{STATUTS.a_prevoir.label}</span>
                                                            </SelectItem>
                                                            <SelectItem value="reserve">
                                                                <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUTS.reserve.color}`}>{STATUTS.reserve.label}</span>
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <Badge variant="outline" className={`text-xs border ${(STATUTS[item.statut] || STATUTS.a_prevoir).color}`}>
                                                        {(STATUTS[item.statut] || STATUTS.a_prevoir).label}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            {isAdmin && (
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                                        onClick={() => setConfirmDelete({ id: item.id, name: item.client_nom })}
                                                        data-testid={`gm-delete-${item.id}`}
                                                        title="Supprimer (corbeille)"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
                <AlertDialogContent data-testid="gm-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce garde-meuble ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Le garde-meuble de <strong>{confirmDelete?.name}</strong> sera déplacé dans la corbeille. Vous pourrez le restaurer si besoin depuis l'admin.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="gm-delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={handleDelete}
                            data-testid="gm-delete-confirm-btn"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
